/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { TenantTypesEnum } from "Constants/TenantTypes";
import { IM365CopilotBenefit } from "Interfaces/Benefits/IM365CopilotBenefit";
import { IFilterValue } from "Interfaces/IFilterValue";

import {
  M365_COPILOT_BENEFITS_SEARCH,
  M365_COPILOT_BENEFITS_SEARCH_FAILURE,
  M365_COPILOT_BENEFITS_SEARCH_RESET,
  M365_COPILOT_BENEFITS_SEARCH_SUCCESS,
  M365_COPILOT_FILTER_VALUE_ADD,
  M365_COPILOT_FILTER_VALUE_REMOVE,
  M365_COPILOT_FILTER_VALUE_UPDATE,
  UPDATE_M365_COPILOT_LISTING_CURRENT_PAGE,
  UPDATE_M365_COPILOT_LISTING_PAGE_SIZE,
} from "./M365CopilotBenefitActionType";

/**
 * Interface for searching M365 Copilot Benefit
 */
export interface ISearchM365CopilotBenefit {
  type: typeof M365_COPILOT_BENEFITS_SEARCH;
  tenant: TenantTypesEnum;
  searchText: string;
  selectedBenefitsFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
}

/**
 * Interface for searching M365 Copilot Benefit Success
 */
export interface ISearchM365CopilotBenefitSuccess {
  type: typeof M365_COPILOT_BENEFITS_SEARCH_SUCCESS;
  benefits: IM365CopilotBenefit[];
  filteredCount: number;
}

/**
 * Interface for searching M365 Copilot Benefit Fail
 */
export interface ISearchM365CopilotBenefitFail {
  type: typeof M365_COPILOT_BENEFITS_SEARCH_FAILURE;
  error: string;
}

/**
 * Interface for resetting M365 Copilot Benefit State
 */
export interface IResetM365CopilotBenefit {
  type: typeof M365_COPILOT_BENEFITS_SEARCH_RESET;
}

/**
 * Interface for adding M365 Copilot Filter Value
 */
export interface IAddM365CopilotFilterValue {
  type: typeof M365_COPILOT_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Interface for removing M365 Copilot Filter Value
 */
export interface IRemoveM365CopilotFilterValue {
  type: typeof M365_COPILOT_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Interface for updating M365 Copilot Filter Value
 */
export interface IUpdateM365CopilotFilterValue {
  type: typeof M365_COPILOT_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Interface for updating M365 Copilot Current Page
 */
export interface IUpdateM365CopilotCurrentPage {
  type: typeof UPDATE_M365_COPILOT_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Interface for updating M365 Copilot Page Size
 */
export interface IUpdateM365CopilotPageSize {
  type: typeof UPDATE_M365_COPILOT_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action creator for M365_COPILOT_BENEFITS_SEARCH
 */
export function searchM365CopilotBenefit(
  tenant: TenantTypesEnum,
  searchText: string,
  selectedBenefitsFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number
): ISearchM365CopilotBenefit {
  return {
    type: M365_COPILOT_BENEFITS_SEARCH,
    tenant,
    searchText,
    selectedBenefitsFilterValues,
    searchPage,
    pageSize,
  };
}

/**
 * Action creator for M365_COPILOT_BENEFITS_SEARCH_SUCCESS
 */
export function searchM365CopilotBenefitSuccess(
  benefits: IM365CopilotBenefit[],
  filteredCount: number
): ISearchM365CopilotBenefitSuccess {
  return {
    type: M365_COPILOT_BENEFITS_SEARCH_SUCCESS,
    benefits,
    filteredCount,
  };
}

/**
 * Action creator for M365_COPILOT_BENEFITS_SEARCH_FAILURE
 */
export function searchM365CopilotBenefitFail(error: string): ISearchM365CopilotBenefitFail {
  return {
    type: M365_COPILOT_BENEFITS_SEARCH_FAILURE,
    error,
  };
}

/**
 * Action creator for M365_COPILOT_BENEFITS_SEARCH_RESET
 */
export function resetM365CopilotBenefit(): IResetM365CopilotBenefit {
  return {
    type: M365_COPILOT_BENEFITS_SEARCH_RESET,
  };
}

/**
 * Action creator for M365_COPILOT_FILTER_VALUE_ADD
 */
export function addM365CopilotFilterValue(filterValue: IFilterValue): IAddM365CopilotFilterValue {
  return {
    type: M365_COPILOT_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for M365_COPILOT_FILTER_VALUE_REMOVE
 */
export function removeM365CopilotFilterValue(
  filterValue: IFilterValue
): IRemoveM365CopilotFilterValue {
  return {
    type: M365_COPILOT_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for M365_COPILOT_FILTER_VALUE_UPDATE
 */
export function updateM365CopilotFilterValue(
  filterValue: IFilterValue
): IUpdateM365CopilotFilterValue {
  return {
    type: M365_COPILOT_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for UPDATE_M365_COPILOT_LISTING_CURRENT_PAGE
 */
export function updateM365CopilotCurrentPage(currentPage: number): IUpdateM365CopilotCurrentPage {
  return {
    type: UPDATE_M365_COPILOT_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_M365_COPILOT_LISTING_PAGE_SIZE
 */
export function updateM365CopilotPageSize(pageSize: number): IUpdateM365CopilotPageSize {
  return {
    type: UPDATE_M365_COPILOT_LISTING_PAGE_SIZE,
    pageSize,
  };
}

export type M365CopilotBenefitActionTypes =
  | ISearchM365CopilotBenefit
  | ISearchM365CopilotBenefitSuccess
  | ISearchM365CopilotBenefitFail
  | IResetM365CopilotBenefit
  | IAddM365CopilotFilterValue
  | IRemoveM365CopilotFilterValue
  | IUpdateM365CopilotFilterValue
  | IUpdateM365CopilotCurrentPage
  | IUpdateM365CopilotPageSize;
